import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Plus, ChevronRight, Download, Play, Square, Trash2, ArrowLeft, RefreshCw, Check, X, GripVertical, Save, Edit3, LineChart as LineChartIcon, Wand2, Eraser } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "../lib/api";

const GOLD = "#C8A832";

function fmt(d) {
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
}
function iso(days) { return new Date(Date.now() + days * 86400000).toISOString(); }

/* ==================== Rounds list ==================== */
export function CutAdminRounds() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try { setRounds(await api.cutAdminListRounds()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setCreating(true);
    try {
      await api.cutAdminCreateRound({ name: name.trim(), startDate: new Date().toISOString(), endDate: iso(7), maxBrands: 8 });
      setName(""); await load();
      toast.success("Round created");
    } catch { toast.error("Could not create"); } finally { setCreating(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="cut-admin-rounds">
      <div className="flex items-center gap-3 mb-2">
        <Trophy size={22} style={{ color: GOLD }} />
        <h1 className="text-2xl font-semibold tracking-tight">The Cut · Rounds</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">Create rounds, approve brands, edit their products, and export live leaderboards.</p>

      <div className="lp-card p-5 mb-6 flex gap-3 flex-wrap items-end" data-testid="cut-create-round">
        <div className="flex-1 min-w-[220px]">
          <label className="lp-label">New round name</label>
          <input className="lp-input" data-testid="cut-round-name-input" placeholder="e.g. Round 02 — Winter Drop" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="lp-help">Starts as draft (endDate = 7 days from now). You can edit after.</div>
        </div>
        <button onClick={create} disabled={creating} className="lp-btn-primary" data-testid="cut-create-round-btn">
          <Plus size={14} /> Create round
        </button>
      </div>

      {loading ? <div className="text-sm text-gray-500 py-8 text-center">Loading…</div> :
        rounds.length === 0 ? <div className="lp-card p-10 text-center text-sm text-gray-500">No rounds yet — create one above.</div> :
        <div className="space-y-3">
          {rounds.map((r) => (
            <Link key={r.id} to={`/app/the-cut/${r.id}`} className="lp-card p-5 flex items-center justify-between hover:border-gray-300 transition-colors" data-testid={`cut-round-row-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-gray-900">{r.name}</div>
                  <StatusPill status={r.status} />
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {r.brandCount || 0} brands · {r.voteCount || 0} votes · closes {fmt(r.endDate)}
                </div>
              </div>
              <ChevronRight size={18} className="text-gray-300" />
            </Link>
          ))}
        </div>
      }
    </div>
  );
}

/* ==================== Round detail (brands list + leaderboard) ==================== */
export function CutAdminRoundDetail() {
  const { id } = useParams();
  const [round, setRound] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBrand, setNewBrand] = useState("");
  const [seedingVotes, setSeedingVotes] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rounds, list] = await Promise.all([
        api.cutAdminListRounds(),
        api.cutAdminListBrands(id),
      ]);
      setRound(rounds.find((r) => r.id === id) || null);
      setBrands(list);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh leaderboard every 10s
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (s) => {
    try { await api.cutAdminUpdateRound(id, { status: s }); toast.success(`Round ${s}`); load(); }
    catch { toast.error("Could not update"); }
  };
  const closeRound = async () => {
    if (!window.confirm("Close voting and email winner's voters?")) return;
    try {
      const r = await api.cutAdminCloseRound(id);
      toast.success(`Closed · winner: ${r.winner || "—"} · ${r.emailsSent} emails sent`);
      load();
    } catch { toast.error("Could not close"); }
  };
  const setBrandStatus = async (bid, status) => {
    await api.cutAdminUpdateBrand(bid, { status });
    load();
  };
  const removeBrand = async (bid) => {
    if (!window.confirm("Delete this brand and its products & votes?")) return;
    await api.cutAdminDeleteBrand(bid); load();
  };
  const addBrand = async () => {
    if (!newBrand.trim()) return;
    await api.cutAdminCreateBrand(id, { brandName: newBrand.trim(), status: "approved" });
    setNewBrand(""); load();
  };
  const seedVotes = async () => {
    if (!window.confirm("Add simulated demo votes to every brand in this round?")) return;
    setSeedingVotes(true);
    try {
      const r = await api.cutAdminSeedVotes(id);
      toast.success(`${r.votesCreated} simulated votes added`);
      load();
    } catch { toast.error("Could not seed votes"); }
    finally { setSeedingVotes(false); }
  };
  const clearVotes = async () => {
    if (!window.confirm("Remove all SIMULATED votes? Real votes are kept.")) return;
    setSeedingVotes(true);
    try {
      const r = await api.cutAdminClearVotes(id);
      toast.success(`${r.deleted} simulated votes cleared`);
      load();
    } catch { toast.error("Could not clear votes"); }
    finally { setSeedingVotes(false); }
  };
  const exportCsv = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/the-cut/rounds/${id}/leaderboard.csv`, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `the-cut-${round?.name || id}.csv`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (!round) return <div className="p-10 text-sm text-gray-500">Round not found · <Link to="/app/the-cut" className="text-[#d0473e]">Back</Link></div>;

  const sorted = [...brands].sort((a, b) => (b.totalVotes || 0) - (a.totalVotes || 0));

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="cut-admin-round-detail">
      <Link to="/app/the-cut" className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3" data-testid="cut-admin-back">
        <ArrowLeft size={12} /> All rounds
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold tracking-tight">{round.name}</h1>
            <StatusPill status={round.status} />
          </div>
          <div className="text-xs text-gray-500">Closes {fmt(round.endDate)} · {brands.length} brands · Auto-refreshes every 10s <RefreshCw size={10} className="inline" /></div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {round.status !== "active" && <button onClick={() => setStatus("active")} className="lp-btn-secondary text-xs" data-testid="cut-round-activate"><Play size={12} /> Open voting</button>}
          {round.status === "active" && <button onClick={closeRound} className="lp-btn-secondary text-xs" style={{ color: "#9b1c2a" }} data-testid="cut-round-close"><Square size={12} /> Close & announce winner</button>}
          <button onClick={exportCsv} className="lp-btn-secondary text-xs" data-testid="cut-export-csv"><Download size={12} /> Export CSV</button>
          <button onClick={seedVotes} disabled={seedingVotes} className="lp-btn-secondary text-xs" data-testid="cut-seed-votes" title="Add simulated demo votes"><Wand2 size={12} /> {seedingVotes ? "Working…" : "Simulate votes"}</button>
          <button onClick={clearVotes} disabled={seedingVotes} className="lp-btn-secondary text-xs" data-testid="cut-clear-votes" title="Remove simulated votes"><Eraser size={12} /> Clear sim</button>
        </div>
      </div>

      <div className="lp-card p-4 mb-5 flex gap-2 items-center flex-wrap" data-testid="cut-add-brand-row">
        <input className="lp-input flex-1 min-w-[220px]" data-testid="cut-new-brand-input" placeholder="Add a brand manually (name only)" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} />
        <button onClick={addBrand} className="lp-btn-primary text-xs" data-testid="cut-add-brand-btn"><Plus size={12} /> Add</button>
      </div>

      <RoundAnalyticsChart roundId={id} />
      <RankTimelineChart roundId={id} />

      <div className="space-y-3">
        {sorted.map((b, i) => (
          <div key={b.id} className="lp-card p-4" data-testid={`cut-brand-row-${b.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg text-white font-bold flex items-center justify-center shrink-0" style={{ background: i === 0 ? GOLD : "#111", color: i === 0 ? "#0a0a0a" : "#fff" }}>
                #{i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/app/the-cut/${id}/${b.id}`} className="font-medium text-gray-900 hover:underline">{b.brandName}</Link>
                  <StatusPill status={b.status} />
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {b.productCount || 0} products · <strong className="text-gray-800">{b.totalVotes || 0}</strong> votes · <Link to={`/the-cut/${b.slug}`} target="_blank" className="text-[#d0473e]">/the-cut/{b.slug}</Link>
                </div>
              </div>
              <div className="flex gap-1.5">
                {b.status !== "approved" && <button onClick={() => setBrandStatus(b.id, "approved")} className="lp-btn-secondary text-xs" data-testid={`cut-approve-${b.id}`}><Check size={12} /> Approve</button>}
                {b.status !== "declined" && <button onClick={() => setBrandStatus(b.id, "declined")} className="lp-btn-secondary text-xs" style={{ color: "#9b1c2a" }} data-testid={`cut-decline-${b.id}`}><X size={12} /> Decline</button>}
                <Link to={`/app/the-cut/${id}/${b.id}`} className="lp-btn-secondary text-xs" data-testid={`cut-edit-${b.id}`}><Edit3 size={12} /> Edit</Link>
                <button onClick={() => removeBrand(b.id)} className="lp-btn-secondary text-xs" style={{ color: "#7a1e1e" }} data-testid={`cut-delete-brand-${b.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {sorted.length === 0 && <div className="lp-card p-10 text-center text-sm text-gray-500">No brands yet.</div>}
      </div>
    </div>
  );
}

/* ==================== Brand editor (products — unlimited) ==================== */
export function CutAdminBrandEditor() {
  const { id: roundId, brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [brands, prods] = await Promise.all([
        api.cutAdminListBrands(roundId),
        api.cutAdminListProducts(brandId),
      ]);
      setBrand(brands.find((x) => x.id === brandId) || null);
      setProducts(prods);
    } finally { setLoading(false); }
  }, [roundId, brandId]);
  useEffect(() => { load(); }, [load]);

  const updateBrand = async (patch) => {
    setBrand((b) => ({ ...b, ...patch }));
    await api.cutAdminUpdateBrand(brandId, patch);
  };
  const addProduct = async () => {
    const p = await api.cutAdminCreateProduct(brandId, { name: "New product", price: 0, category: "Uncategorised" });
    setProducts([...products, { ...p, votes: 0 }]);
  };
  const updateProduct = async (pid, patch) => {
    setProducts((arr) => arr.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
    await api.cutAdminUpdateProduct(pid, patch);
  };
  const removeProduct = async (pid) => {
    if (!window.confirm("Delete this product & its votes?")) return;
    await api.cutAdminDeleteProduct(pid);
    setProducts((arr) => arr.filter((p) => p.id !== pid));
  };

  // ---------------- Drag-and-drop reorder ----------------
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set
    try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* ignore */ }
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); setOverIdx(i); };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setOverIdx(null);
    if (from === null || from === i) return;
    const arr = [...products];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    setProducts(arr);
    try {
      await api.cutAdminReorderProducts(brandId, arr.map((p) => p.id));
      toast.success("Order saved");
    } catch { toast.error("Reorder failed"); load(); }
  };

  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (!brand) return <div className="p-10 text-sm text-gray-500">Brand not found.</div>;

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="cut-admin-brand-editor">
      <Link to={`/app/the-cut/${roundId}`} className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3" data-testid="cut-brand-back">
        <ArrowLeft size={12} /> Round brands
      </Link>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{brand.brandName}</h1>
        <div className="text-xs text-gray-500 mt-1">
          Public URL: <Link to={`/the-cut/${brand.slug}`} target="_blank" className="text-[#d0473e]">/the-cut/{brand.slug}</Link> · {brand.totalVotes || 0} votes
        </div>
      </div>

      <div className="lp-card p-5 mb-6" data-testid="cut-brand-fields">
        <div className="lp-section-label mb-3">Brand details</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <BrandField label="Brand name" value={brand.brandName} onSave={(v) => updateBrand({ brandName: v })} />
          <BrandField label="Slug (URL)" value={brand.slug} onSave={(v) => updateBrand({ slug: v })} />
          <BrandField label="Tagline" value={brand.tagline} onSave={(v) => updateBrand({ tagline: v })} />
          <BrandField label="Instagram" value={brand.instagram} onSave={(v) => updateBrand({ instagram: v })} />
          <BrandField label="Followers" value={brand.followers} onSave={(v) => updateBrand({ followers: v })} />
          <BrandField label="Website" value={brand.website} onSave={(v) => updateBrand({ website: v })} />
          <div className="sm:col-span-2">
            <BrandField label="Winner pre-order URL" value={brand.preorderUrl} onSave={(v) => updateBrand({ preorderUrl: v })} note="Where should the winner-announcement email send voters? Falls back to /the-cut/{slug} if empty." />
          </div>
        </div>
        <div className="mt-4">
          <BrandField label="Brand story" value={brand.story} onSave={(v) => updateBrand({ story: v })} multiline />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="lp-section-label">Products ({products.length})</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Drag the ⋮⋮ handle to reorder — the storefront ranks by votes but this sets the initial display order.</div>
        </div>
        <button onClick={addProduct} className="lp-btn-primary text-xs" data-testid="cut-add-product-btn">
          <Plus size={12} /> Add product
        </button>
      </div>

      <div className="space-y-3">
        {products.map((p, i) => {
          const isDragging = dragIdx === i;
          const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
          return (
          <div key={p.id}
            draggable
            onDragStart={onDragStart(i)}
            onDragOver={onDragOver(i)}
            onDragEnd={onDragEnd}
            onDrop={onDrop(i)}
            className="lp-card p-4"
            data-testid={`cut-product-row-${p.id}`}
            style={{
              opacity: isDragging ? 0.4 : 1,
              transform: isDragging ? "scale(.98)" : "none",
              borderColor: isOver ? "#0a0a0a" : undefined,
              boxShadow: isOver ? "0 -3px 0 0 #0a0a0a inset" : undefined,
              transition: "opacity .15s, transform .15s, border-color .15s, box-shadow .15s",
              cursor: "grab",
            }}>
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center gap-1 pt-1 select-none" style={{ cursor: "grab" }} aria-label="Drag to reorder">
                <GripVertical size={16} className="text-gray-300" />
                <div className="text-[10px] text-gray-400 tabular-nums">{i + 1}</div>
              </div>
              <img src={p.imageUrl || ""} alt="" className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0" onError={(e) => (e.target.style.opacity = 0)} />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <BrandField label="Name" value={p.name} onSave={(v) => updateProduct(p.id, { name: v })} />
                <BrandField label="Category" value={p.category} onSave={(v) => updateProduct(p.id, { category: v })} />
                <BrandField label="Price" value={String(p.price || 0)} onSave={(v) => updateProduct(p.id, { price: parseFloat(v) || 0 })} />
                <BrandField label="Image URL" value={p.imageUrl} onSave={(v) => updateProduct(p.id, { imageUrl: v })} />
                <div className="sm:col-span-2">
                  <BrandField label="Description" value={p.description} onSave={(v) => updateProduct(p.id, { description: v })} multiline />
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold" style={{ color: GOLD }}>{p.votes || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">votes</div>
                <button onClick={() => removeProduct(p.id)} className="text-xs text-red-600 hover:underline" data-testid={`cut-delete-product-${p.id}`}>Delete</button>
              </div>
            </div>
          </div>
          );
        })}
        {products.length === 0 && <div className="lp-card p-10 text-center text-sm text-gray-500">No products — add the first one.</div>}
      </div>
    </div>
  );
}

function BrandField({ label, value, onSave, multiline, note }) {
  const [v, setV] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(value || ""); setDirty(false); }, [value]);
  const save = async () => {
    if (!dirty) return;
    try { await onSave(v); setDirty(false); toast.success(`${label} saved`); } catch { toast.error("Save failed"); }
  };
  return (
    <div>
      <label className="lp-label">{label}</label>
      <div className="flex gap-2 items-start">
        {multiline
          ? <textarea className="lp-input" rows={3} value={v} onChange={(e) => { setV(e.target.value); setDirty(true); }} onBlur={save} />
          : <input className="lp-input" value={v} onChange={(e) => { setV(e.target.value); setDirty(true); }} onBlur={save} />
        }
        {dirty && <button onClick={save} className="lp-btn-primary text-xs shrink-0"><Save size={11} /></button>}
      </div>
      {note && <div className="lp-help">{note}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    draft: { bg: "#f3f4f6", color: "#6b7280" },
    active: { bg: "#eaf3ea", color: "#2d7a2d" },
    closed: { bg: "#f3f4f6", color: "#6b7280" },
    pending: { bg: "#fff3e5", color: "#a35010" },
    approved: { bg: "#eaf3ea", color: "#2d7a2d" },
    declined: { bg: "#fdecea", color: "#9b1c2a" },
  }[status] || { bg: "#f3f4f6", color: "#6b7280" };
  return <span className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: styles.bg, color: styles.color }}>{status}</span>;
}

/* ==================== Rank timeline chart ==================== */
function RankTimelineChart({ roundId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { setData(await api.cutAdminRankTimeline(roundId)); } finally { setLoading(false); }
  }, [roundId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data) return null;
  const hours = data.hours || [];
  const series = data.series || [];
  if (hours.length < 2 || series.length === 0) {
    return (
      <div className="lp-card p-6 mb-5" data-testid="cut-rank-timeline">
        <div className="flex items-center gap-2 mb-1">
          <LineChartIcon size={14} className="text-gray-400" />
          <div className="lp-section-label">Leaderboard swings</div>
        </div>
        <div className="text-xs text-gray-400 mt-2">Needs at least 2 vote buckets to render — comes to life once brands start trading positions.</div>
      </div>
    );
  }
  const rows = hours.map((h) => {
    const row = { t: h };
    series.forEach((s) => {
      const point = s.points.find((p) => p.t === h);
      row[s.brandName] = point ? point.rank : null;
    });
    return row;
  });
  const fmtT = (h) => { try { return new Date(h).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }); } catch { return h; } };
  // Invert Y so rank 1 shows at top
  const maxRank = series.length;

  return (
    <div className="lp-card p-6 mb-5" data-testid="cut-rank-timeline">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <LineChartIcon size={14} className="text-gray-500" />
          <div className="lp-section-label">Leaderboard swings</div>
        </div>
        <div className="text-xs text-gray-400">Rank position hour-by-hour · #1 at the top</div>
      </div>
      <div style={{ width: "100%", height: 240, marginTop: 12 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 4, right: 16, bottom: 4, left: -12 }}>
            <XAxis dataKey="t" tickFormatter={fmtT} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis reversed domain={[1, maxRank]} allowDecimals={false} ticks={Array.from({ length: maxRank }, (_, i) => i + 1)} tickFormatter={(v) => `#${v}`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={34} />
            <Tooltip labelFormatter={fmtT} formatter={(v) => `#${v}`} contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12, color: "#fff" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
            {series.map((s, i) => (
              <Line key={s.brandId} type="stepAfter" dataKey={s.brandName} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} animationDuration={400} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ==================== Analytics chart ==================== */
const SERIES_COLORS = ["#C8A832", "#d0473e", "#3b82f6", "#10b981", "#a855f7", "#f59e0b", "#ec4899", "#6b7280"];

function RoundAnalyticsChart({ roundId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { setData(await api.cutAdminRoundAnalytics(roundId)); } finally { setLoading(false); }
  }, [roundId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return null;
  const hours = data?.hours || [];
  const series = data?.series || [];
  if (hours.length === 0 || series.length === 0) {
    return (
      <div className="lp-card p-6 mb-5" data-testid="cut-round-analytics">
        <div className="flex items-center gap-2 mb-1">
          <LineChartIcon size={14} className="text-gray-400" />
          <div className="lp-section-label">Vote momentum</div>
        </div>
        <div className="text-xs text-gray-400 mt-2">No votes yet — the chart will appear once brands start collecting votes.</div>
      </div>
    );
  }
  // Reshape into recharts-friendly row format: [{t, "Kadi Apparel": 3, "Noxxon": 1, ...}]
  const rows = hours.map((h) => {
    const row = { t: h };
    series.forEach((s) => {
      const point = s.points.find((p) => p.t === h);
      row[s.brandName] = point ? point.votes : 0;
    });
    return row;
  });
  const fmtT = (h) => { try { return new Date(h).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }); } catch { return h; } };

  return (
    <div className="lp-card p-6 mb-5" data-testid="cut-round-analytics">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <LineChartIcon size={14} className="text-gray-500" />
          <div className="lp-section-label">Vote momentum</div>
        </div>
        <div className="text-xs text-gray-400">{data.totalVotes} total · hourly buckets · refreshes every 15s</div>
      </div>
      <div style={{ width: "100%", height: 240, marginTop: 12 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 4, right: 16, bottom: 4, left: -12 }}>
            <XAxis dataKey="t" tickFormatter={fmtT} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={30} />
            <Tooltip labelFormatter={fmtT} contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12, color: "#fff" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
            {series.map((s, i) => (
              <Line key={s.brandId} type="monotone" dataKey={s.brandName} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={false} animationDuration={400} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
